import { useEffect, useState } from "react";
import {
  Bell,
  Clock,
  MapPin,
  Pencil,
  Plus,
  Ticket,
  Trash2,
  X,
  Zap,
} from "lucide-react";
import { db } from "../db.ts";
import { api } from "../api.ts";
import { Spinner } from "../components/Spinner.tsx";
import { Button, Input, Label, Select } from "../components/ui/index.ts";
import { type Category, categoryEmoji, cats } from "../../../src/category.ts";

export interface UserSubscription {
  id: string;
  phone: string;
  country: string;
  categories: string[];
  blacklistedCategories?: string[];
  location?: string;
  radiusKm?: number;
  pushRRule?: string;
  pushLocalHour?: number;
  pushLocalMinute?: number;
  instant?: boolean;
  ticketAlerts?: string;
  minTasteMatch?: number;
  maxEvents?: number;
  blacklistedWords?: string[];
  whitelistedWords?: string[];
  conversationId?: string;
  waitingEventIds?: string[];
  lastAggregatedPushAt?: number;
}

type Country = "Israel" | "US" | "Germany" | "Argentina";

const countries: { value: Country; label: string }[] = [
  { value: "Israel", label: "Israel" },
  { value: "US", label: "US" },
  { value: "Germany", label: "Germany" },
  { value: "Argentina", label: "Argentina" },
];

const scheduleLabel = (sub: UserSubscription) => {
  if (sub.instant) return "⚡ Instant alerts on match";
  const rrule = sub.pushRRule ?? "FREQ=DAILY";
  const hour = sub.pushLocalHour ?? 18;
  const minute = sub.pushLocalMinute ?? 0;
  const isFrequent = rrule.startsWith("FREQ=MINUTELY") ||
    rrule.startsWith("FREQ=HOURLY");
  return isFrequent
    ? rrule
    : `${rrule} at ${hour}:${String(minute).padStart(2, "0")}`;
};

export const SubscriptionsPageContent = () => {
  const { user } = db.useAuth();
  const [subscriptions, setSubscriptions] = useState<UserSubscription[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editingSub, setEditingSub] = useState<UserSubscription | null>(null);
  const [isCreating, setIsCreating] = useState(false);

  // Form states
  const [formCountry, setFormCountry] = useState<Country>("Israel");
  const [formLocation, setFormLocation] = useState("");
  const [formRadius, setFormRadius] = useState<number>(20);
  const [formCategories, setFormCategories] = useState<string[]>([]);
  const [formInstant, setFormInstant] = useState(true);
  const [formMinMatch, setFormMinMatch] = useState<number>(85);
  const [formTicketAlerts, setFormTicketAlerts] = useState<string>("");
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const fetchSubscriptions = () => {
    if (!user) return;
    setLoading(true);
    setError(null);
    api({
      endpoint: "getUserSubscriptions",
      payload: {},
    })
      .then((subs) => {
        setSubscriptions(subs);
      })
      .catch((err: unknown) => {
        console.error("Failed to load subscriptions:", err);
        setError(
          err instanceof Error ? err.message : "Failed to load subscriptions",
        );
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    fetchSubscriptions();
  }, [user?.id]);

  const openCreateModal = () => {
    setEditingSub(null);
    setFormCountry("Israel");
    setFormLocation("");
    setFormRadius(20);
    setFormCategories([]);
    setFormInstant(true);
    setFormMinMatch(85);
    setFormTicketAlerts("");
    setIsCreating(true);
  };

  const openEditModal = (sub: UserSubscription) => {
    setEditingSub(sub);
    setFormCountry((sub.country as Country) || "Israel");
    setFormLocation(sub.location || "");
    setFormRadius(sub.radiusKm || 20);
    setFormCategories(sub.categories || []);
    setFormInstant(sub.instant ?? true);
    setFormMinMatch(sub.minTasteMatch ?? 85);
    setFormTicketAlerts(sub.ticketAlerts || "");
    setIsCreating(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api({
        endpoint: "upsertUserSubscription",
        payload: {
          id: editingSub?.id,
          country: formCountry,
          categories: formCategories,
          location: formLocation.trim() || undefined,
          radiusKm: formRadius,
          instant: formInstant,
          minTasteMatch: formMinMatch,
          ticketAlerts: formTicketAlerts || undefined,
        },
      });
      setIsCreating(false);
      setEditingSub(null);
      fetchSubscriptions();
    } catch (err) {
      console.error("Failed to save subscription:", err);
      alert(
        err instanceof Error ? err.message : "Failed to save subscription",
      );
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm("Are you sure you want to delete this subscription?")) return;
    setDeletingId(id);
    try {
      await api({
        endpoint: "deleteUserSubscription",
        payload: { id },
      });
      fetchSubscriptions();
    } catch (err) {
      console.error("Failed to delete subscription:", err);
      alert(
        err instanceof Error ? err.message : "Failed to delete subscription",
      );
    } finally {
      setDeletingId(null);
    }
  };

  const toggleCategory = (cat: string) => {
    setFormCategories((prev) =>
      prev.includes(cat) ? prev.filter((c) => c !== cat) : [...prev, cat]
    );
  };

  if (loading) {
    return (
      <div className="container mx-auto max-w-3xl px-4 py-16 flex justify-center items-center">
        <Spinner className="h-8 w-8 text-purple-500" />
      </div>
    );
  }

  return (
    <div className="container mx-auto max-w-3xl px-4 py-8 space-y-6">
      {/* Top Header & Actions */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-extrabold text-white tracking-tight flex items-center gap-2.5">
            <Bell className="w-6 h-6 text-purple-400" />
            <span>Subscriptions</span>
          </h1>
          <p className="text-gray-400 text-sm mt-1">
            Configure automated WhatsApp push alerts and personalized digests
          </p>
        </div>

        <button
          type="button"
          onClick={openCreateModal}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500 text-white text-xs sm:text-sm font-semibold shadow-lg shadow-purple-500/20 transition active:scale-95"
        >
          <Plus size={16} />
          <span>New Alert</span>
        </button>
      </div>

      {error && (
        <div className="p-4 rounded-xl bg-red-500/10 border border-red-500/20 text-red-300 text-sm">
          {error}
        </div>
      )}

      {/* Subscriptions List */}
      {subscriptions.length === 0
        ? (
          <div className="glass-card p-10 rounded-2xl border border-white/10 text-center space-y-4">
            <div className="w-14 h-14 rounded-full bg-purple-500/10 border border-purple-500/20 flex items-center justify-center mx-auto text-purple-400">
              <Bell className="w-7 h-7" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-white">
                No active subscriptions
              </h3>
              <p className="text-sm text-gray-400 mt-1 max-w-md mx-auto">
                Set up alerts to receive WhatsApp digests and instant push
                notifications when new events matching your vibe are announced.
              </p>
            </div>
            <button
              type="button"
              onClick={openCreateModal}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-purple-600 hover:bg-purple-500 text-white text-sm font-semibold transition shadow-md shadow-purple-500/20"
            >
              <Plus size={16} />
              <span>Create Your First Alert</span>
            </button>
          </div>
        )
        : (
          <div className="space-y-4">
            {subscriptions.map((sub) => (
              <div
                key={sub.id}
                className="glass-card p-5 rounded-2xl border border-white/10 hover:border-purple-500/30 transition-all space-y-4"
              >
                {/* Top Row */}
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="px-2.5 py-1 rounded-full text-xs font-bold bg-purple-500/15 text-purple-300 border border-purple-500/30">
                      {sub.country}
                    </span>
                    {sub.instant
                      ? (
                        <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-bold bg-emerald-500/15 text-emerald-300 border border-emerald-500/30">
                          <Zap className="w-3.5 h-3.5" />
                          Instant Alerts
                        </span>
                      )
                      : (
                        <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-white/5 text-gray-300 border border-white/10">
                          <Clock className="w-3.5 h-3.5" />
                          {scheduleLabel(sub)}
                        </span>
                      )}
                    {sub.minTasteMatch !== undefined && (
                      <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-amber-500/15 text-amber-300 border border-amber-500/30">
                        ✨ Min {sub.minTasteMatch}% Match
                      </span>
                    )}
                    {sub.ticketAlerts && (
                      <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-blue-500/15 text-blue-300 border border-blue-500/30">
                        <Ticket className="w-3.5 h-3.5" />
                        Tickets: {sub.ticketAlerts}
                      </span>
                    )}
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-1.5 shrink-0">
                    <button
                      type="button"
                      onClick={() => openEditModal(sub)}
                      className="p-1.5 rounded-lg bg-white/5 hover:bg-white/10 text-gray-300 hover:text-white transition border border-white/10"
                      title="Edit"
                    >
                      <Pencil size={14} />
                    </button>
                    <button
                      type="button"
                      onClick={() => handleDelete(sub.id)}
                      disabled={deletingId === sub.id}
                      className="p-1.5 rounded-lg bg-red-500/10 hover:bg-red-500/20 text-red-400 hover:text-red-300 transition border border-red-500/20 disabled:opacity-50"
                      title="Delete"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>

                {/* Location */}
                {sub.location && (
                  <div className="flex items-center gap-1.5 text-xs text-gray-300">
                    <MapPin className="w-3.5 h-3.5 text-purple-400 shrink-0" />
                    <span>
                      <strong>{sub.location}</strong>{" "}
                      ({sub.radiusKm ?? 20}km radius)
                    </span>
                  </div>
                )}

                {/* Categories */}
                <div>
                  <div className="text-[11px] font-bold uppercase tracking-wider text-gray-400 mb-1.5">
                    Subscribed Categories
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {sub.categories && sub.categories.length > 0
                      ? (
                        sub.categories.map((cat) => {
                          const emoji = categoryEmoji(cat as Category);
                          return (
                            <span
                              key={cat}
                              className="text-xs px-2.5 py-0.5 rounded-full bg-purple-500/15 text-purple-300 border border-purple-500/25 font-medium"
                            >
                              {emoji ? `${emoji} ${cat}` : cat}
                            </span>
                          );
                        })
                      )
                      : (
                        <span className="text-xs text-gray-400 italic">
                          All categories (purely taste-match based)
                        </span>
                      )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

      {/* Create / Edit Modal */}
      {isCreating && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm animate-fade-in">
          <div className="glass-card w-full max-w-lg p-6 sm:p-8 rounded-3xl border border-white/15 shadow-2xl space-y-6 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-bold text-white">
                {editingSub ? "Edit Subscription" : "New Push Alert"}
              </h2>
              <button
                type="button"
                onClick={() => setIsCreating(false)}
                className="p-1 text-gray-400 hover:text-white rounded-lg transition"
              >
                <X size={20} />
              </button>
            </div>

            <div className="space-y-4">
              {/* Country */}
              <div className="space-y-1.5">
                <Label htmlFor="sub-country">Country</Label>
                <Select
                  id="sub-country"
                  value={formCountry}
                  onChange={(e) =>
                    setFormCountry(e.currentTarget.value as Country)}
                  options={countries}
                  className="w-full"
                />
              </div>

              {/* Location & Radius */}
              <div className="space-y-1.5">
                <Label htmlFor="sub-location">Location / City (Optional)</Label>
                <Input
                  id="sub-location"
                  placeholder="e.g. Tel Aviv, Jerusalem, Berlin"
                  value={formLocation}
                  onChange={(e) => setFormLocation(e.target.value)}
                />
              </div>

              {formLocation.trim() && (
                <div className="space-y-1.5">
                  <div className="flex justify-between text-xs text-gray-400">
                    <Label htmlFor="sub-radius">Search Radius</Label>
                    <span>{formRadius} km</span>
                  </div>
                  <input
                    id="sub-radius"
                    type="range"
                    min="5"
                    max="100"
                    step="5"
                    value={formRadius}
                    onChange={(e) => setFormRadius(Number(e.target.value))}
                    className="w-full accent-purple-500"
                  />
                </div>
              )}

              {/* Instant vs Scheduled */}
              <div className="flex items-center justify-between p-3.5 rounded-xl bg-white/5 border border-white/10">
                <div>
                  <p className="text-sm font-semibold text-white">
                    Instant Alerts
                  </p>
                  <p className="text-xs text-gray-400">
                    Notify immediately when a high taste-match event lands
                  </p>
                </div>
                <input
                  type="checkbox"
                  checked={formInstant}
                  onChange={(e) => setFormInstant(e.target.checked)}
                  className="w-5 h-5 accent-purple-600 rounded cursor-pointer"
                />
              </div>

              {/* Min Taste Match */}
              <div className="space-y-1.5">
                <div className="flex justify-between text-xs text-gray-400">
                  <Label htmlFor="sub-taste">Minimum Taste Match Score</Label>
                  <span className="font-semibold text-amber-300">
                    {formMinMatch}%
                  </span>
                </div>
                <input
                  id="sub-taste"
                  type="range"
                  min="50"
                  max="95"
                  step="5"
                  value={formMinMatch}
                  onChange={(e) => setFormMinMatch(Number(e.target.value))}
                  className="w-full accent-purple-500"
                />
              </div>

              {/* Ticket Trade Alerts */}
              <div className="space-y-1.5">
                <Label htmlFor="sub-tickets">Ticket Trade Alerts</Label>
                <Select
                  id="sub-tickets"
                  value={formTicketAlerts}
                  onChange={(e) => setFormTicketAlerts(e.currentTarget.value)}
                  options={[
                    { value: "", label: "No ticket trade alerts" },
                    { value: "buy", label: "Buyers looking for tickets" },
                    { value: "sell", label: "Sellers offering tickets" },
                    { value: "both", label: "Both buy & sell alerts" },
                  ]}
                  className="w-full"
                />
              </div>

              {/* Categories */}
              <div className="space-y-2 pt-1">
                <div className="flex justify-between items-center">
                  <Label>Categories (Leave empty for all categories)</Label>
                  {formCategories.length > 0 && (
                    <button
                      type="button"
                      onClick={() => setFormCategories([])}
                      className="text-[11px] text-purple-400 hover:text-purple-300"
                    >
                      Clear all
                    </button>
                  )}
                </div>
                <div className="flex flex-wrap gap-1.5 max-h-48 overflow-y-auto p-1">
                  {Object.entries(cats).map(([cat, { emoji, display }]) => {
                    const active = formCategories.includes(cat);
                    return (
                      <button
                        key={cat}
                        type="button"
                        onClick={() => toggleCategory(cat)}
                        className={`px-3 py-1 text-xs rounded-full border font-medium transition ${
                          active
                            ? "bg-purple-600 border-purple-500 text-white shadow-md shadow-purple-500/20"
                            : "bg-white/5 border-white/10 text-gray-300 hover:bg-white/10"
                        }`}
                      >
                        {emoji ? `${emoji} ${display.en}` : display.en}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Modal Actions */}
            <div className="flex items-center gap-3 pt-4 border-t border-white/10">
              <Button
                variant="secondary"
                onClick={() => setIsCreating(false)}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={handleSave}
                loading={saving}
                className="flex-1"
              >
                Save Alert
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
